import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  List,
  ListItem,
  ListIcon,
  Badge,
  Divider,
  useColorModeValue,
  Tooltip,
  Icon,
} from '@chakra-ui/react'
import { FaCheck } from 'react-icons/fa'
import { FiInfo } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { SubscriptionPlan } from '../../types/subscription'

interface PricingCardProps {
  plan: SubscriptionPlan
  isSelected?: boolean
  onSelect: () => void
  isLoading?: boolean
}

const getFeatureTooltip = (feature: string, t: any): string | undefined => {
  // Check if the feature contains any of the tooltip keywords
  const tooltipKeys = {
    'credit': 'subscription.tooltips.credits',
    'crédit': 'subscription.tooltips.credits',
    'teleexpertise': 'subscription.tooltips.smartTeleexpertise',
    'téléexpertise': 'subscription.tooltips.smartTeleexpertise',
    'literature': 'subscription.tooltips.literatureWatch',
    'bibliographique': 'subscription.tooltips.literatureWatch',
  }

  for (const [key, tooltipPath] of Object.entries(tooltipKeys)) {
    if (feature.toLowerCase().includes(key.toLowerCase())) {
      return t(tooltipPath)
    }
  }
  return undefined
}

export default function PricingCard({ plan, isSelected, onSelect, isLoading }: PricingCardProps) {
  const { t } = useTranslation()
  const borderColor = useColorModeValue('gray.200', 'gray.700')

  const isRecommended = plan.recommended
  const accentColor = isRecommended ? '#f59e0b' : '#6366f1'

  return (
    <Box
      position="relative"
      borderRadius="2xl"
      overflow="hidden"
      border="1px solid"
      borderColor={isRecommended ? 'orange.200' : isSelected ? 'purple.300' : borderColor}
      bg="white"
      boxShadow={isRecommended ? '0 20px 60px rgba(251,146,60,0.15)' : 'sm'}
      transition="all 0.25s"
      _hover={{ transform: 'translateY(-4px)', boxShadow: isRecommended ? '0 28px 64px rgba(251,146,60,0.22)' : 'lg' }}
      transform={isRecommended ? 'scale(1.03)' : undefined}
    >
      {/* Accent top bar */}
      <Box
        h="4px"
        bgGradient={isRecommended
          ? 'linear(90deg, #f59e0b, #d946ef)'
          : 'linear(90deg, #6366f1, #8b5cf6)'}
      />

      {plan.badge && (
        <Badge
          position="absolute"
          top="20px"
          right="20px"
          bg={isRecommended ? 'linear-gradient(135deg, #f59e0b, #d946ef)' : 'gray.100'}
          color={isRecommended ? 'white' : 'gray.600'}
          fontSize="2xs"
          fontWeight="bold"
          px={2.5}
          py={1}
          borderRadius="full"
          letterSpacing="0.04em"
        >
          {plan.badge}
        </Badge>
      )}

      <Box p={8}>
        <VStack spacing={0} align="stretch">
          {/* Name */}
          <Text
            fontSize="xl"
            fontWeight="bold"
            color="gray.800"
            mb={5}
            pr={plan.badge ? 20 : 0}
          >
            {plan.name}
          </Text>

          {/* Price */}
          <Box mb={2}>
            {plan.price !== null ? (
              <HStack align="baseline" spacing={1}>
                <Text
                  fontSize="4xl"
                  fontWeight="extrabold"
                  color={isRecommended ? 'orange.500' : 'gray.900'}
                  lineHeight="1"
                >
                  {plan.price}€
                </Text>
                <Text fontSize="sm" color="gray.400" fontWeight="medium">/mois</Text>
              </HStack>
            ) : (
              <Text fontSize="3xl" fontWeight="extrabold" color="gray.900" lineHeight="1">
                Sur devis
              </Text>
            )}
            <Text fontSize="xs" color="gray.400" mt={1}>
              {plan.price !== null ? 'HT · par professionnel' : 'Tarification au volume'}
            </Text>
          </Box>

          {/* Description */}
          <Text fontSize="sm" color="gray.500" mb={6} lineHeight="1.6">
            {plan.description}
          </Text>

          {/* CTA */}
          <Button
            size="md"
            w="100%"
            onClick={onSelect}
            isLoading={isLoading}
            loadingText="Traitement..."
            isDisabled={isSelected}
            mb={6}
            borderRadius="xl"
            fontWeight="semibold"
            bgGradient={isRecommended && !isSelected ? 'linear(to-r, orange.400, pink.500)' : undefined}
            colorScheme={isRecommended ? 'orange' : 'purple'}
            variant={isSelected ? 'outline' : 'solid'}
            color={isRecommended && !isSelected ? 'white' : undefined}
            _hover={isRecommended && !isSelected ? {
              bgGradient: 'linear(to-r, orange.500, pink.600)',
              transform: 'translateY(-1px)',
              boxShadow: 'md',
            } : undefined}
          >
            {isSelected ? '✓ Offre actuelle' : plan.ctaText}
          </Button>

          {/* Divider */}
          <Box borderTop="1px solid" borderColor="gray.100" mb={5} />

          {/* Features */}
          <List spacing={2.5}>
            {plan.features.map((feature, index) => {
              const tooltipText = getFeatureTooltip(feature, t)
              return (
                <ListItem key={index} display="flex" alignItems="flex-start">
                  <Box
                    w="18px"
                    h="18px"
                    borderRadius="full"
                    bg={isRecommended ? 'orange.50' : 'purple.50'}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                    mt="1px"
                    mr={2.5}
                  >
                    <ListIcon
                      as={FaCheck}
                      color={isRecommended ? 'orange.400' : 'purple.400'}
                      m={0}
                      boxSize="8px"
                    />
                  </Box>
                  <HStack spacing={1} align="flex-start" flex={1}>
                    <Text fontSize="sm" color="gray.600" lineHeight="1.5">{feature}</Text>
                    {tooltipText && (
                      <Tooltip label={tooltipText} placement="top" hasArrow>
                        <Box as="span" display="inline-flex" cursor="help">
                          <Icon as={FiInfo} boxSize={3} color="gray.400" mt="2px" />
                        </Box>
                      </Tooltip>
                    )}
                  </HStack>
                </ListItem>
              )
            })}
          </List>
        </VStack>
      </Box>
    </Box>
  )
}
